import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Helmet } from 'react-helmet';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { AddToWatchlistIcon } from '@sorare/core/src/atoms/icons/AddToWatchlistIcon';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Breadcrumb } from '@sorare/core/src/components/breadcrumb';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { NewHeader } from '@sorare/core/src/components/seo/NewHeader';
import {
  FOOTBALL,
  FOOTBALL_CLUBS_SLUG,
  FOOTBALL_PLAYERS_SLUG,
  FOOTBALL_SCOUTING_CENTER,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useIntlCountries } from '@sorare/core/src/hooks/useIntlCountries';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { positionShortNames } from '@sorare/core/src/lib/players';

import { LastOwnedCardsByPlayer } from '@sorare/marketplace/src/components/player/LastOwnedCardsByPlayer';
import { AddToWatchlistButton } from '@sorare/marketplace/src/components/watchlists/AddToWatchlistButton';

import { PlayerHeaderRow } from './PlayerHeaderRow';
import type { FootballPlayerHeader_anyPlayer } from './__generated__/index.graphql';

const StyledFullViewportScroll = styled(FullViewportScroll)`
  gap: var(--triple-unit);
`;

const Item = styled(LabelM)`
  display: flex;
  align-items: center;
  gap: var(--half-unit);
`;

export const FootballPlayerHeader = ({
  player,
}: {
  player: FootballPlayerHeader_anyPlayer;
}) => {
  const { up: isTabletOrAbove } = useScreenSize('tablet');
  const { formatNumber, formatMessage, formatDate } = useIntlContext();
  const countries = useIntlCountries();
  const playersBreadcrumb = [
    {
      to: FOOTBALL,
      name: 'Football',
    },
    {
      to: FOOTBALL_SCOUTING_CENTER,
      name: formatMessage(glossary.players),
    },
    { name: player.displayName },
  ];
  if (!isType(player, 'Player')) return null;
  const position = player.anyPositions?.[0] || player.position;

  return (
    <>
      <NewHeader
        id={player.slug}
        breadcrumb={
          <Breadcrumb
            sections={playersBreadcrumb}
            gap={isTabletOrAbove ? 1.5 : 0.5}
          />
        }
        displayName={player.displayName}
        pictureUrl={player.squaredPictureUrl}
        color={player.activeClub?.styleGuideColor}
        details={
          <StyledFullViewportScroll>
            {!!player.rankingForPositionLeague && (
              <Item color="var(--c-nd-600)">
                {!!player.activeClub?.domesticLeague && (
                  <LeagueAvatar
                    size={16}
                    league={player.activeClub.domesticLeague}
                  />
                )}
                <span>#{player.rankingForPositionLeague || 0}</span>
                <FormattedMessage {...positionShortNames[position]} />
              </Item>
            )}

            <Item color="var(--c-nd-600)">
              <FormattedNumber
                value={player.subscriptionsCount}
                maximumFractionDigits={-2}
              />
              <span>
                <FormattedMessage {...glossary.followers} />
              </span>
            </Item>
            <LastOwnedCardsByPlayer anyPlayer={player} withLabel />
          </StyledFullViewportScroll>
        }
        subscription={player}
        addToWatchlistNode={
          <AddToWatchlistButton
            anyPlayer={player}
            size="medium"
            color="quaternary"
            startIcon={<AddToWatchlistIcon />}
            blur
          />
        }
        metricsRow={<PlayerHeaderRow anyPlayer={player} />}
      />
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'Person',
              name: player.displayName,
              givenName: player.firstName,
              familyName: player.lastName,
              ...(player.birthDate
                ? {
                    birthDate: formatDate(player.birthDate, {
                      timeZone: 'UTC',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    }),
                  }
                : {}),
              ...(player.height
                ? {
                    height: `${formatNumber(player.height / 1e2, {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })} m`,
                  }
                : {}),
              nationality: countries.find(n => n.value === player?.country.code)
                ?.label,
              image: player.squaredPictureUrl,
              mainEntityOfPage: generatePath(FOOTBALL_PLAYERS_SLUG, {
                slug: player.slug,
              }),
              affiliation: [
                ...(player.activeClub
                  ? [
                      {
                        '@type': 'SportsTeam',
                        name: player.activeClub.name,
                        mainEntityOfPage: generatePath(FOOTBALL_CLUBS_SLUG, {
                          slug: player.activeClub.slug,
                        }),
                      },
                    ]
                  : []),
                ...(player.activeNationalTeam
                  ? [
                      {
                        '@type': 'SportsTeam',
                        name: player.activeNationalTeam.name,
                      },
                    ]
                  : []),
              ],
            }),
          },
        ]}
      />
    </>
  );
};

FootballPlayerHeader.fragments = {
  anyPlayer: gql`
    fragment FootballPlayerHeader_anyPlayer on AnyPlayerInterface {
      slug
      displayName
      firstName
      lastName
      anyPositions
      squaredPictureUrl
      height
      subscriptionsCount
      activeClub {
        slug
        name
        styleGuideColor
        pictureUrl
        domesticLeague {
          slug
          name
          pictureUrl
        }
      }
      activeNationalTeam {
        slug
        name
      }
      country {
        slug
        code
      }
      ... on Player {
        slug
        birthDate
        rankingForPositionLeague
      }
      ...NewHeader_subscription
      ...LastOwnedCards_anyPlayer
      ...PlayerHeaderRow_anyPlayer
      ...AddToWatchlistButton_anyPlayer
    }
    ${AddToWatchlistButton.fragments.anyPlayer}
    ${NewHeader.fragments.subscription}
    ${LastOwnedCardsByPlayer.fragments.anyPlayer}
    ${PlayerHeaderRow.fragments.anyPlayer}
  ` as TypedDocumentNode<FootballPlayerHeader_anyPlayer>,
};
